import { useCallback, useEffect, useState } from 'react';
import {
  listBeerIngestionRunsForSource,
  listBreweryBeerSources,
  runBeerSourceMonitor,
  recreateBeerSourceMonitor,
  setTrustedBreweryBeerSource,
  updateBreweryBeerSourceStatus,
  upsertBreweryBeerSource,
} from '../services/breweryBeerSourcesService';

const MONITORING_STATUS_PENDING_ACTIONS = {
  paused: 'pause',
  retired: 'retire',
  active: 'reactivate',
};

function pendingActionForMonitoringStatus(sourceId, monitoringStatus) {
  return {
    sourceId,
    action: MONITORING_STATUS_PENDING_ACTIONS[monitoringStatus] || monitoringStatus,
  };
}

/**
 * Loads and mutates brewery beer source registry records via backend callables.
 */
function useBreweryBeerSources(breweryId) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const refresh = useCallback(async (options = {}) => {
    const keepVisible = options.keepVisible === true;
    if (!breweryId) {
      setSources([]);
      setLoading(false);
      return;
    }

    try {
      if (!keepVisible) {
        setLoading(true);
      }
      setError(null);
      const rows = await listBreweryBeerSources(breweryId);
      setSources(rows);
    } catch (err) {
      console.error('Error fetching brewery beer sources:', err);
      setError(err?.message || 'Failed to load sources');
      setSources([]);
    } finally {
      setLoading(false);
    }
  }, [breweryId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runMutation = useCallback(async (mutationFn, nextPendingAction = null) => {
    try {
      setActionLoading(true);
      setPendingAction(nextPendingAction);
      setActionError(null);
      const updatedSource = await mutationFn();
      await refresh({ keepVisible: true });
      return updatedSource;
    } catch (err) {
      console.error('Beer source mutation failed:', err);
      const message = err?.message || 'Source update failed';
      setActionError(message);
      throw err;
    } finally {
      setActionLoading(false);
      setPendingAction(null);
    }
  }, [refresh]);

  const addOrUpdateSource = useCallback(
    (payload) => runMutation(() => upsertBreweryBeerSource({ breweryId, ...payload })),
    [breweryId, runMutation],
  );

  const markTrusted = useCallback(
    (sourceId) =>
      runMutation(
        () => setTrustedBreweryBeerSource(breweryId, sourceId),
        { sourceId, action: 'trust' },
      ),
    [breweryId, runMutation],
  );

  const updateStatus = useCallback(
    (sourceId, monitoringStatus) =>
      runMutation(
        () => updateBreweryBeerSourceStatus(sourceId, monitoringStatus),
        pendingActionForMonitoringStatus(sourceId, monitoringStatus),
      ),
    [runMutation],
  );

  const checkNow = useCallback(
    (sourceId) =>
      runMutation(
        () => runBeerSourceMonitor(sourceId),
        { sourceId, action: 'check-now' },
      ),
    [runMutation],
  );

  const recreateMonitor = useCallback(
    (sourceId) =>
      runMutation(
        () => recreateBeerSourceMonitor(sourceId),
        { sourceId, action: 'recreate-monitor' },
      ),
    [runMutation],
  );

  return {
    sources,
    loading,
    error,
    actionError,
    actionLoading,
    pendingAction,
    refresh,
    addOrUpdateSource,
    markTrusted,
    updateStatus,
    checkNow,
    recreateMonitor,
  };
}

export async function fetchLatestIngestionRun(sourceId) {
  const runs = await listBeerIngestionRunsForSource(sourceId, 1);
  return runs[0] ?? null;
}

export default useBreweryBeerSources;
